/**
 * Gig mode auto-scale: computes the largest font size where the longest
 * chord string in the chart fits within the available column width.
 *
 * Uses OffscreenCanvas.measureText() — synchronous, no DOM layout thrash.
 */

const FONT_FAMILY = 'monospace';

/**
 * Effective fraction of column width available per chord, by subdivision count.
 * Accounts for diagonal/X dividers eating into available space.
 *   1 chord → full width (82% safety margin)
 *   2 chords → diagonal split, each chord ≈ 44%
 *   3 chords → X-divider triangular zones ≈ 38%
 *   4 chords → X-divider quadrants ≈ 38%
 */
const SUBDIVISION_WIDTH_RATIOS = [0.82, 0.44, 0.38, 0.38];

/**
 * Binary-searches for the largest integer font size (px) where `text`
 * fits within `maxWidth` pixels using the gig mode font.
 */
function binarySearchFontSize(
  text: string,
  maxWidth: number,
  minSize: number,
  maxSize: number
): number {
  if (!text) return maxSize;

  // OffscreenCanvas is available in all modern browsers and in Vite builds
  let canvas: OffscreenCanvas | HTMLCanvasElement;
  let ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
  try {
    canvas = new OffscreenCanvas(1, 1);
    ctx = (canvas as OffscreenCanvas).getContext('2d');
  } catch {
    // Fallback for environments without OffscreenCanvas
    canvas = document.createElement('canvas');
    ctx = (canvas as HTMLCanvasElement).getContext('2d');
  }
  if (!ctx) return Math.floor((minSize + maxSize) / 2);

  let lo = minSize;
  let hi = maxSize;
  let best = minSize;

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    ctx.font = `bold ${mid}px ${FONT_FAMILY}`;
    const measured = ctx.measureText(text).width;
    if (measured <= maxWidth) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best;
}

/**
 * Main export: compute the optimal gig font size for the whole chart.
 * Accounts for both chord length AND subdivision count (2/3/4 chords per measure
 * each get less available width due to diagonal/X dividers).
 *
 * @param chart      Array of measures with chords
 * @param columnWidth Pixel width of one column cell (after padding/gaps)
 * @param rowHeight   Pixel height of one row cell (used for height-based cap)
 * @returns Optimal font size in px, clamped to [16, 72]
 */
export function computeOptimalGigFontSize(
  chart: Array<{ chords: (string | null)[] }>,
  columnWidth: number,
  rowHeight: number
): number {
  if (!chart.length || columnWidth <= 0) return 24;

  // Height cap: font shouldn't exceed ~70% of row height
  const heightCap = Math.floor(rowHeight * 0.70);

  // Global base is computed only from single-chord measures.
  // Multi-chord measures apply their own subdivision ratio in GigMeasureContent.
  // This prevents a single complex 4-chord measure from shrinking every simple measure.
  const singleChordMeasures = chart.filter(m => m.chords.length === 1);
  const measuresToScan = singleChordMeasures.length > 0 ? singleChordMeasures : chart;

  let optimal = 72;

  for (const measure of measuresToScan) {
    // When falling back to all measures, use the appropriate subdivision ratio
    const sub = singleChordMeasures.length > 0 ? 1 : Math.max(1, Math.min(4, measure.chords.length));
    const width = columnWidth * SUBDIVISION_WIDTH_RATIOS[sub - 1];

    for (const chord of measure.chords) {
      if (!chord || chord === '/' || chord === '%' || chord === 'x') continue;
      const size = binarySearchFontSize(chord, width, 16, 72);
      if (size < optimal) optimal = size;
    }
  }

  return Math.max(16, Math.min(optimal, heightCap));
}
